import { z } from 'zod';

import { defineEnvelope } from './envelope.js';

/**
 * Event tags conocidos del MVP (CLAUDE.md §8).
 *
 * Convención: dot.notation jerárquica, inglés.
 *   - `<domain>.<action>.intent`    → cliente envía intención.
 *   - `<domain>.<action>.requested` → cliente pide algo no-juego (pair, etc.).
 *   - `<domain>.<action>.accepted`  → servidor confirma outcome.
 *   - `<domain>.<action>.failed`    → servidor rechaza intent/request.
 *
 * Cuando un schema nuevo aparece, se añade aquí y se documenta en
 * docs/architecture/realtime-contracts.md.
 */
export const RealtimeEventTag = {
  PairRequested: 'juramentario.pair.requested',
  PairAccepted: 'juramentario.pair.accepted',
  PairFailed: 'juramentario.pair.failed',
  SessionError: 'session.error',
  ReconciliationReport: 'netcode.reconciliation.report',
} as const;

export type RealtimeEventTagName = (typeof RealtimeEventTag)[keyof typeof RealtimeEventTag];

// ---------- juramentario.pair.requested ----------

export const PairRequestedPayloadSchema = z
  .object({
    pairSessionToken: z.string().min(16),
    role: z.enum(['pc', 'mobile']),
  })
  .strict();

export const PairRequestedEnvelopeSchema = defineEnvelope(
  RealtimeEventTag.PairRequested,
  PairRequestedPayloadSchema,
);

// ---------- juramentario.pair.accepted ----------

export const PairAcceptedPayloadSchema = z
  .object({
    seatId: z.string().uuid(),
    alias: z.string().min(1).max(32),
    pcConnected: z.boolean(),
    mobileConnected: z.boolean(),
  })
  .strict();

export const PairAcceptedEnvelopeSchema = defineEnvelope(
  RealtimeEventTag.PairAccepted,
  PairAcceptedPayloadSchema,
);

// ---------- juramentario.pair.failed ----------

export const PairFailedReason = z.enum([
  'invalid_token',
  'expired_token',
  'seat_full',
  'rate_limited',
  'unknown',
]);

export const PairFailedPayloadSchema = z
  .object({
    reason: PairFailedReason,
    /** Mensaje localizable (clave de i18n, no texto traducido). */
    i18nKey: z.string().min(1).max(64),
  })
  .strict();

export const PairFailedEnvelopeSchema = defineEnvelope(
  RealtimeEventTag.PairFailed,
  PairFailedPayloadSchema,
);

// ---------- session.error ----------

export const SessionErrorPayloadSchema = z
  .object({
    code: z.string().min(1).max(64),
    i18nKey: z.string().min(1).max(64),
  })
  .strict();

export const SessionErrorEnvelopeSchema = defineEnvelope(
  RealtimeEventTag.SessionError,
  SessionErrorPayloadSchema,
);

// ---------- netcode.reconciliation.report ----------

export const ReconciliationReportPayloadSchema = z
  .object({
    seatId: z.string().uuid(),
    /** Distancia entre la posición del último snapshot y la rendereada. */
    deltaUnits: z.number().nonnegative().finite(),
    /** TickId del snapshot autoritativo usado en la comparación. */
    serverTickId: z.number().int().nonnegative(),
  })
  .strict();

export const ReconciliationReportEnvelopeSchema = defineEnvelope(
  RealtimeEventTag.ReconciliationReport,
  ReconciliationReportPayloadSchema,
);

export type PairRequestedPayload = z.infer<typeof PairRequestedPayloadSchema>;
export type PairAcceptedPayload = z.infer<typeof PairAcceptedPayloadSchema>;
export type PairFailedPayload = z.infer<typeof PairFailedPayloadSchema>;
export type SessionErrorPayload = z.infer<typeof SessionErrorPayloadSchema>;
export type ReconciliationReportPayload = z.infer<typeof ReconciliationReportPayloadSchema>;
